import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0C0F] text-white">
      {/* Navigation Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[rgba(15,15,20,0.95)] backdrop-blur-lg' : 'bg-transparent'
        }`}
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <div className="max-w-[1600px] mx-auto px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/57cf4798-34b3-4d2d-9fac-06ab28a63e2d.png" 
              alt="DARK d.o.o. Logo" 
              className="h-10 w-auto object-contain"
            />
            <h1 className="text-[28px] font-bold tracking-[2px] text-white">DARK d.o.o.</h1>
          </div>
          <nav className="flex items-center gap-10">
            {['home', 'about', 'products', 'features', 'contact'].map((item, index) => (
              <div key={item} className="flex items-center gap-10">
                <button
                  onClick={() => scrollToSection(item)}
                  className="text-[16px] text-white hover:text-[#00FFF0] transition-colors duration-300 capitalize cursor-pointer whitespace-nowrap"
                >
                  {item}
                </button>
                {index < 4 && (
                  <div className="w-[1px] h-5 bg-[rgba(255,255,255,0.1)]"></div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="min-h-screen pt-32 pb-20 px-20">
        <div className="max-w-[1600px] mx-auto flex items-center gap-16">
          {/* Left Content Area */}
          <div className="w-[40%] space-y-16">
            <div className="inline-block px-6 py-2 rounded-full border border-[#00FFF0] bg-transparent">
              <span className="text-[11px] uppercase tracking-[3px] text-[#00FFF0]">
                iOS DEVELOPMENT EXPERTS
              </span>
            </div>
            
            <h2 className="text-[72px] font-extrabold leading-[1.1] text-white">
              Modern Applications for iOS Platform
            </h2>
            
            <p className="text-[20px] leading-[1.6] text-[#A0A0AA]">
              Enhancing user experience and mobile device security through innovative VPN and Cleaner solutions
            </p>
            
            <div className="w-[3px] h-[120px] bg-[#00FFF0]"></div>
          </div>

          {/* Right Visual Area */}
          <div className="w-[60%] h-[600px]">
            <div className="w-full h-full rounded-[32px] bg-gradient-to-br from-[#3C1450] to-[#141E3C] flex items-center justify-center overflow-hidden relative">
              <img
                src="https://public.readdy.ai/ai/img_res/8409e69ec1c3723a1ba36466c0618a88.jpg"
                alt="iOS Development Visualization"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="min-h-screen bg-[#19191E] py-32 px-20 relative">
        <div className="max-w-[1600px] mx-auto">
          {/* Top Title Area */}
          <div className="ml-20">
            <p className="text-[12px] uppercase tracking-[3px] text-[#00FFF0] mb-6">
              WHO WE ARE
            </p>
            <h3 className="text-[56px] font-bold text-white mb-4">Expert Team</h3>
            <p className="text-[32px] italic text-[#B48CC8] font-serif">
              Building Tomorrow's iOS Solutions
            </p>
          </div>

          {/* Description Block */}
          <div className="mt-32 ml-auto mr-20 max-w-[480px]">
            <p className="text-[18px] leading-[1.8] text-[#B4B4BE]">
              At DARK d.o.o., we are a dedicated team of iOS development experts committed to creating cutting-edge mobile applications. Our focus on security, performance, and user experience drives us to deliver products that truly make a difference in how people interact with their devices.
            </p>
            <p className="text-[18px] leading-[1.8] text-[#B4B4BE] mt-6">
              With years of experience in the iOS ecosystem, we understand the importance of building applications that are not only functional but also intuitive and secure. Our VPN and Cleaner products represent our commitment to excellence and innovation.
            </p>
          </div>

          {/* Visual Element */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] opacity-20">
            <div className="w-full h-full border-4 border-[#00FFF0] rounded-[60px] rotate-45"></div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="min-h-screen bg-[#121216] py-32 px-20">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Area */}
          <div className="ml-20 mb-20">
            <h3 className="text-[64px] font-bold text-white">Our Products</h3>
            <h3 className="text-[64px] font-bold text-[#FF3366]">Innovation</h3>
            <p className="text-[16px] text-[#A0A0AA] mt-8 max-w-[500px]">
              Discover our suite of iOS applications designed to protect, optimize, and enhance your mobile experience
            </p>
          </div>

          {/* Product Cards */}
          <div className="flex gap-8 overflow-x-auto pb-8 scrollbar-hide">
            {/* VPN Product Card */}
            <div className="min-w-[380px] h-[520px] bg-[#1C1C23] rounded-[24px] border border-[rgba(255,255,255,0.08)] flex flex-col overflow-hidden">
              <div className="h-[60%] relative bg-gradient-to-br from-[#0066FF] to-[#00FFF0] p-6">
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20iOS%20VPN%20application%20interface%20mockup%20on%20iPhone%20screen%20showing%20secure%20connection%20with%20glowing%20blue%20cyan%20shield%20icon%20and%20world%20map%20visualization%2C%20modern%20dark%20UI%20design%2C%20professional%20security%20app%20screenshot%2C%20clean%20minimalist%20interface%2C%20high%20quality%20product%20visualization%2C%20simple%20gradient%20background&width=380&height=312&seq=vpn-product-001&orientation=portrait"
                  alt="VPN Product"
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-6 left-6 w-[80px] h-[80px] bg-white rounded-[20px] shadow-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-[40px] text-[#0066FF]"></i>
                </div>
              </div>
              <div className="h-[40%] p-6 flex flex-col justify-between">
                <div>
                  <h4 className="text-[28px] font-bold text-white mb-3">VPN Pro</h4>
                  <p className="text-[15px] leading-[1.5] text-[#A0A0AA]">
                    Secure your connection with military-grade encryption and access content worldwide
                  </p>
                </div>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="min-w-[380px] h-[520px] bg-[#1C1C23] rounded-[24px] border border-[rgba(255,255,255,0.08)] flex flex-col overflow-hidden">
              <div className="h-[60%] relative bg-gradient-to-br from-[#9D4EDD] to-[#FF006E] p-6">
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20iOS%20Cleaner%20application%20interface%20mockup%20on%20iPhone%20screen%20showing%20storage%20optimization%20with%20purple%20pink%20gradient%20circular%20charts%20and%20file%20management%20visualization%2C%20modern%20clean%20UI%20design%2C%20professional%20utility%20app%20screenshot%2C%20minimalist%20interface%2C%20high%20quality%20product%20visualization%2C%20simple%20gradient%20background&width=380&height=312&seq=cleaner-product-001&orientation=portrait"
                  alt="Cleaner Product"
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-6 left-6 w-[80px] h-[80px] bg-white rounded-[20px] shadow-lg flex items-center justify-center">
                  <i className="ri-delete-bin-line text-[40px] text-[#9D4EDD]"></i>
                </div>
              </div>
              <div className="h-[40%] p-6 flex flex-col justify-between">
                <div>
                  <h4 className="text-[28px] font-bold text-white mb-3">Cleaner Plus</h4>
                  <p className="text-[15px] leading-[1.5] text-[#A0A0AA]">
                    Optimize your device performance and free up valuable storage space instantly
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Product Card */}
            <div className="min-w-[380px] h-[520px] bg-[#1C1C23] rounded-[24px] border border-[rgba(255,255,255,0.08)] flex flex-col overflow-hidden">
              <div className="h-[60%] relative bg-gradient-to-br from-[#06FFA5] to-[#00B4D8] p-6">
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20iOS%20security%20application%20interface%20mockup%20on%20iPhone%20screen%20showing%20privacy%20protection%20features%20with%20green%20cyan%20gradient%20lock%20icons%20and%20security%20dashboard%2C%20modern%20sleek%20UI%20design%2C%20professional%20security%20app%20screenshot%2C%20minimalist%20interface%2C%20high%20quality%20product%20visualization%2C%20simple%20gradient%20background&width=380&height=312&seq=security-product-001&orientation=portrait"
                  alt="Security Product"
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-6 left-6 w-[80px] h-[80px] bg-white rounded-[20px] shadow-lg flex items-center justify-center">
                  <i className="ri-lock-line text-[40px] text-[#06FFA5]"></i>
                </div>
              </div>
              <div className="h-[40%] p-6 flex flex-col justify-between">
                <div>
                  <h4 className="text-[28px] font-bold text-white mb-3">Privacy Guard</h4>
                  <p className="text-[15px] leading-[1.5] text-[#A0A0AA]">
                    Comprehensive privacy protection for your personal data and online activities
                  </p>
                </div>
              </div>
            </div>

            {/* Partially Visible Card */}
            <div className="min-w-[380px] h-[520px] bg-[#1C1C23] rounded-[24px] border border-[rgba(255,255,255,0.08)] flex flex-col overflow-hidden opacity-60">
              <div className="h-[60%] relative bg-gradient-to-br from-[#FF6B35] to-[#F7931E] p-6">
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20iOS%20performance%20application%20interface%20mockup%20on%20iPhone%20screen%20showing%20speed%20optimization%20with%20orange%20gradient%20performance%20graphs%20and%20system%20monitoring%2C%20modern%20dynamic%20UI%20design%2C%20professional%20utility%20app%20screenshot%2C%20minimalist%20interface%2C%20high%20quality%20product%20visualization%2C%20simple%20gradient%20background&width=380&height=312&seq=performance-product-001&orientation=portrait"
                  alt="Performance Product"
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-6 left-6 w-[80px] h-[80px] bg-white rounded-[20px] shadow-lg flex items-center justify-center">
                  <i className="ri-speed-line text-[40px] text-[#FF6B35]"></i>
                </div>
              </div>
              <div className="h-[40%] p-6 flex flex-col justify-between">
                <div>
                  <h4 className="text-[28px] font-bold text-white mb-3">Speed Boost</h4>
                  <p className="text-[15px] leading-[1.5] text-[#A0A0AA]">
                    Accelerate your device performance with intelligent optimization algorithms
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="min-h-screen bg-[#F0F0F2] py-32 px-20">
        <div className="max-w-[1600px] mx-auto">
          {/* Header Area */}
          <div className="flex items-start justify-between mb-20">
            <div>
              <h3 className="text-[48px] font-light text-[#A0A0AA]">Platform</h3>
              <h3 className="text-[52px] font-bold italic text-black font-serif">Features</h3>
            </div>
            <div className="w-[65%]">
              <p className="text-[17px] leading-[1.7] text-[#50505A]">
                Our iOS applications are built with cutting-edge technology to deliver unparalleled performance, security, and user experience. Every feature is carefully crafted to meet the highest standards of quality and reliability.
              </p>
            </div>
          </div>

          {/* Three-Column Grid */}
          <div className="grid grid-cols-3 gap-10">
            {/* Security Feature Card */}
            <div className="bg-white rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="h-[320px] p-6">
                <img
                  src="https://readdy.ai/api/search-image?query=realistic%20iOS%20iPhone%20device%20mockup%20showing%20VPN%20security%20interface%20with%20blue%20shield%20icon%20and%20encrypted%20connection%20visualization%20on%20screen%2C%20professional%20product%20photography%20style%2C%20clean%20white%20background%2C%20high%20quality%20device%20render%2C%20modern%20app%20interface%20design%2C%20premium%20technology%20showcase&width=400&height=320&seq=security-feature-001&orientation=landscape"
                  alt="Security Feature"
                  className="w-full h-full object-cover rounded-[20px]"
                />
              </div>
              <div className="p-6">
                <div className="inline-block px-4 py-2 rounded-[12px] bg-[#DCFFB4] mb-6">
                  <span className="text-[13px] uppercase tracking-[2px] text-[#283C1E] font-medium">
                    SECURITY
                  </span>
                </div>
                <h4 className="text-[26px] font-bold text-black leading-[1.3] mb-4">
                  Military-Grade Encryption Protection
                </h4>
                <p className="text-[15px] leading-[1.6] text-[#50505A]">
                  Advanced encryption protocols ensure your data remains private and secure at all times
                </p>
              </div>
            </div>

            {/* Performance Feature Card */}
            <div className="bg-white rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="h-[320px] p-6 bg-[#0C0C0F] flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=glowing%20cyan%20neon%20shield%20icon%20with%20geometric%20hexagonal%20patterns%20and%20circuit%20board%20lines%20on%20dark%20black%20background%2C%20futuristic%20security%20symbol%2C%20digital%20protection%20visualization%2C%20high%20tech%20abstract%20design%2C%20vibrant%20cyan%20blue%20glow%20effect%2C%20minimalist%20tech%20illustration&width=400&height=320&seq=performance-feature-001&orientation=landscape"
                  alt="Performance Feature"
                  className="w-full h-full object-cover rounded-[20px]"
                />
              </div>
              <div className="p-6">
                <div className="inline-block px-4 py-2 rounded-[12px] bg-[#DCFFB4] mb-6">
                  <span className="text-[13px] uppercase tracking-[2px] text-[#283C1E] font-medium">
                    PERFORMANCE
                  </span>
                </div>
                <h4 className="text-[26px] font-bold text-black leading-[1.3] mb-4">
                  Lightning-Fast Speed Optimization
                </h4>
                <p className="text-[15px] leading-[1.6] text-[#50505A]">
                  Experience seamless performance with intelligent algorithms that maximize device efficiency
                </p>
              </div>
            </div>

            {/* Optimization Feature Card */}
            <div className="bg-white rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="h-[320px] p-6">
                <img
                  src="https://readdy.ai/api/search-image?query=realistic%20iOS%20iPhone%20device%20mockup%20showing%20Cleaner%20app%20interface%20with%20colorful%20storage%20visualization%20circular%20charts%20and%20file%20management%20on%20screen%2C%20professional%20product%20photography%20style%2C%20clean%20white%20background%2C%20high%20quality%20device%20render%2C%20modern%20app%20interface%20design%2C%20premium%20technology%20showcase&width=400&height=320&seq=optimization-feature-001&orientation=landscape"
                  alt="Optimization Feature"
                  className="w-full h-full object-cover rounded-[20px]"
                />
              </div>
              <div className="p-6">
                <div className="inline-block px-4 py-2 rounded-[12px] bg-[#DCFFB4] mb-6">
                  <span className="text-[13px] uppercase tracking-[2px] text-[#283C1E] font-medium">
                    OPTIMIZATION
                  </span>
                </div>
                <h4 className="text-[26px] font-bold text-black leading-[1.3] mb-4">
                  Smart Storage Management System
                </h4>
                <p className="text-[15px] leading-[1.6] text-[#50505A]">
                  Automatically identify and remove unnecessary files to free up valuable storage space
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-screen overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20professional%20workspace%20with%20multiple%20iOS%20devices%20iPhone%20and%20iPad%20on%20minimalist%20desk%20showing%20development%20environment%20and%20code%20on%20screens%2C%20bright%20natural%20lighting%2C%20clean%20contemporary%20office%20setting%2C%20technology%20and%20productivity%20atmosphere%2C%20high%20contrast%20professional%20photography%2C%20sharp%20focus%2C%20premium%20workspace%20aesthetic&width=1920&height=1080&seq=cta-background-001&orientation=landscape"
          alt="Transform Your Mobile Experience"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        {/* Main Title */}
        <div className="absolute top-[28%] left-20">
          <h3 className="text-[76px] font-extrabold text-white leading-[1.1] tracking-[-1px] drop-shadow-lg">
            transform your<br />mobile experience
          </h3>
        </div>

        {/* Description Text */}
        <div className="absolute bottom-[32%] right-24">
          <p className="text-[22px] font-light text-white leading-[1.6] text-right drop-shadow-lg">
            Discover the power of professional<br />iOS applications built for excellence
          </p>
        </div>

        {/* Visual Element */}
        <div className="absolute bottom-24 left-20">
          <div className="w-[220px] h-[64px] bg-white rounded-full flex items-center justify-between px-6 cursor-pointer hover:scale-105 transition-transform duration-300">
            <span className="text-[16px] font-medium text-black whitespace-nowrap">Explore More</span>
            <div className="w-[48px] h-[48px] bg-black rounded-full flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-white text-[24px]"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#F0F0F2] px-10 pt-10 pb-0">
        <div className="max-w-[1600px] mx-auto bg-[#0C0C0F] rounded-t-[40px] px-24 pt-20 pb-16">
          {/* Upper Section */}
          <div className="flex gap-16 mb-20">
            {/* Newsletter Area */}
            <div className="w-[40%]">
              <h4 className="text-[64px] font-light text-white mb-8">Stay Connected</h4>
              <p className="text-[17px] leading-[1.7] text-[#8C8C96] mb-16">
                Subscribe to our newsletter for the latest updates on our iOS applications and exclusive offers
              </p>
              <div className="mb-7">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-[400px] h-[56px] bg-transparent border-0 border-b border-[rgba(255,255,255,0.2)] text-white text-[16px] outline-none placeholder-[#50505A]"
                />
              </div>
              <div className="w-[180px] h-[56px] bg-white rounded-full flex items-center justify-between px-4 cursor-pointer hover:scale-105 transition-transform duration-300">
                <span className="text-[16px] font-medium text-black whitespace-nowrap">Subscribe</span>
                <div className="w-[40px] h-[40px] bg-black rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-[20px]"></i>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="w-[25%]">
              <h5 className="text-[16px] font-medium uppercase tracking-[2px] text-white mb-6">
                Navigate
              </h5>
              <div className="space-y-5">
                {['Home', 'About', 'Products', 'Features'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block text-[15px] text-white underline hover:text-[#00FFF0] transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div className="w-[35%]">
              <h5 className="text-[16px] font-medium uppercase tracking-[2px] text-white mb-6">
                Contact
              </h5>
              <div className="space-y-4 mb-12">
                <div>
                  <p className="text-[13px] text-[#64646E] mb-1">Phone</p>
                  <p className="text-[15px] text-white">+1 4066424463</p>
                </div>
                <div>
                  <p className="text-[13px] text-[#64646E] mb-1">Email</p>
                  <p className="text-[15px] text-white">info@darkdoo.com</p>
                </div>
              </div>
              <div>
                <p className="text-[13px] text-[#64646E] mb-2">Location</p>
                <div className="text-[15px] text-white leading-[1.8] space-y-1">
                  <p>Banija 84</p>
                  <p>Karlovac, 47000</p>
                  <p>Croatia</p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-[rgba(255,255,255,0.08)] mb-8"></div>

          {/* Bottom Bar */}
          <div className="flex items-center justify-between">
            {/* Social Links */}
            <div className="flex items-center gap-8">
              <span className="text-[14px] text-white underline cursor-pointer hover:text-[#00FFF0] transition-colors duration-300 whitespace-nowrap">
                LinkedIn
              </span>
              <span className="text-[14px] text-white underline cursor-pointer hover:text-[#00FFF0] transition-colors duration-300 whitespace-nowrap">
                Twitter
              </span>
              <span className="text-[14px] text-white underline cursor-pointer hover:text-[#00FFF0] transition-colors duration-300 whitespace-nowrap">
                GitHub
              </span>
            </div>

            {/* Copyright */}
            <div className="text-center">
              <p className="text-[14px] text-[#787882]">
                © 2025 DARK d.o.o. All rights reserved
              </p>
            </div>

            {/* Policy Links */}
            <div className="flex items-center gap-7">
              <span className="text-[14px] text-white underline cursor-pointer hover:text-[#00FFF0] transition-colors duration-300 whitespace-nowrap">
                Privacy Policy
              </span>
              <span className="text-[14px] text-white underline cursor-pointer hover:text-[#00FFF0] transition-colors duration-300 whitespace-nowrap">
                Terms of Service
              </span>
            </div>
          </div>

          {/* Quick Link */}
          <div className="mt-8 text-center">
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] text-[#64646E] hover:text-[#00FFF0] transition-colors duration-300 whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
